import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import env from '../../config/env';
import { logout } from '../slices/authSlice';

// Ensure baseUrl is a clean string without trailing slashes
const baseUrl = env.apiUrl.replace(/\/$/, '');

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = (getState() as any).auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    headers.set('Accept', 'application/json');
    // Only set Content-Type if not FormData (FormData needs boundary which browser sets automatically)
    // We'll check this in the query by not setting Content-Type when body is FormData
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  },
  fetchFn: async (input, init) => {
    // If body is FormData, don't set Content-Type header
    if (init?.body instanceof FormData) {
      const headers = new Headers(init.headers);
      headers.delete('Content-Type'); // Let browser set it with boundary
      return fetch(input, { ...init, headers });
    }
    return fetch(input, init);
  },
});

// Custom baseQuery with 401 error handling
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  
  // Check if we got a 401 Unauthorized response
  if (result.error && result.error.status === 401) {
    // Get the URL to determine if it's admin or doctor
    const url = typeof args === 'string' ? args : args.url;
    
    // Don't redirect if we're already on a login endpoint
    if (url && (url.includes('/login') || url.includes('/logout'))) {
      return result;
    }
    
    // Determine login page based on URL path
    let loginPath = '/admin/login';
    if (url && url.includes('/doctor/')) {
      loginPath = '/doctor/login';
    } else if (url && url.includes('/admin/')) {
      loginPath = '/admin/login';
    } else {
      // If URL doesn't indicate, check userType from state
      const state = api.getState() as any;
      const userType = state?.auth?.userType;
      if (userType === 'doctor') {
        loginPath = '/doctor/login';
      }
    }
    
    // Clear auth state
    api.dispatch(logout());
    
    // Redirect to appropriate login page
    const pathname = process.env.REACT_APP_SUB_FOLDER || '';
    const fullPath = pathname ? `${pathname}${loginPath}` : loginPath;
    window.location.href = fullPath;
  }
  
  return result;
};

export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Admin', 'Doctor', 'User', 'Ticket', 'Article', 'Activity', 'Exhibition', 'Item', 'Category', 'Topic', 'Tag', 'Country', 'City', 'Banner', 'SystemUser', 'Role', 'Permission', 'Package', 'Page', 'Contact', 'Partner', 'Property', 'SubCategory', 'AdCategory', 'AdType', 'AdPackage', 'Interest', 'CustomField', 'Ad', 'StrategicAdType', 'CustomFilter', 'VoucherBatch', 'Voucher', 'AgriculturalInfluencer', 'Review', 'Notification', 'PromoCode', 'SystemNotification', 'ActivityLog', 'Transaction', 'BannedKeyword', 'CategoryType', 'AccountType', 'UsersReport', 'AdvertisingReport', 'AdCategoryReport', 'SupplyDemandReport'],
  endpoints: () => ({}),
});
